import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { toast } from 'sonner';
import { Plus, Trash2, CalendarX } from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminBlockedSlots() {
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTimes, setSelectedTimes] = useState([]);
  const [reason, setReason] = useState('');

  // All times except after 12:00 for Saturdays
  const allTimes = [
    "09:00", "09:45", "10:30", "11:15",
    "12:00", "12:45", "13:30", "14:15", "15:00", "15:45", "16:30", "17:15",
    "18:00"
  ];

  const saturdayTimes = ["09:00", "09:45", "10:30", "11:15", "12:00"];

  useEffect(() => {
    fetchBlockedSlots();
  }, []);

  useEffect(() => {
    if (selectedDate) {
      fetchBookedSlotsForDate(format(selectedDate, 'yyyy-MM-dd'));
    }
  }, [selectedDate]);

  const fetchBlockedSlots = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/blocked-slots`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBlockedSlots(response.data);
    } catch (error) {
      toast.error('Failed to fetch blocked slots');
    } finally {
      setLoading(false);
    }
  };

  const fetchBookedSlotsForDate = async (date) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/appointments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const booked = response.data
        .filter(apt => apt.date === date && apt.status !== 'cancelled')
        .map(apt => apt.time);
      setBookedSlots(booked);
    } catch (error) {
      console.error('Failed to fetch booked slots');
    }
  };

  const handleTimeClick = (time, event) => {
    // Check if slot is blocked or booked
    const dateStr = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : '';
    const isBlocked = blockedSlots.some(s => s.date === dateStr && s.time === time);
    const isBooked = bookedSlots.includes(time);
    
    if (isBlocked || isBooked) return; // Can't select blocked/booked slots
    
    if (event.ctrlKey || event.metaKey) {
      // Multi-select with Ctrl/Cmd
      if (selectedTimes.includes(time)) {
        setSelectedTimes(selectedTimes.filter(t => t !== time));
      } else {
        setSelectedTimes([...selectedTimes, time]);
      }
    } else {
      // Single select
      if (selectedTimes.includes(time) && selectedTimes.length === 1) {
        setSelectedTimes([]);
      } else {
        setSelectedTimes([time]);
      }
    }
  };

  const addBlockedSlots = async (e) => {
    e.preventDefault();
    
    if (!selectedDate || selectedTimes.length === 0) {
      toast.error('Please select a date and at least one time');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      
      // Block multiple slots
      await axios.post(`${API}/blocked-slots/bulk`, {
        date: dateStr,
        times: selectedTimes,
        reason: reason || 'Blocked'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`${selectedTimes.length} time slot(s) blocked`);
      setSelectedDate(null);
      setSelectedTimes([]);
      setReason('');
      fetchBlockedSlots();
    } catch (error) {
      toast.error('Failed to block time slots');
    }
  };

  const blockWholeDay = async () => {
    if (!selectedDate) {
      toast.error('Please select a date first');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const dayOfWeek = selectedDate.getDay();
      
      // Use Saturday times if it's Saturday (6), otherwise all times
      const timesToBlock = dayOfWeek === 6 ? saturdayTimes : allTimes;
      
      // Filter out already blocked and booked times
      const availableTimes = timesToBlock.filter(time => {
        const isBlocked = blockedSlots.some(s => s.date === dateStr && s.time === time);
        const isBooked = bookedSlots.includes(time);
        return !isBlocked && !isBooked;
      });

      if (availableTimes.length === 0) {
        toast.info('All slots are already blocked or booked');
        return;
      }

      await axios.post(`${API}/blocked-slots/bulk`, {
        date: dateStr,
        times: availableTimes,
        reason: reason || 'Day blocked'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Blocked ${availableTimes.length} available slots for the day`);
      setSelectedDate(null);
      setSelectedTimes([]);
      setReason('');
      fetchBlockedSlots();
    } catch (error) {
      toast.error('Failed to block the day');
    }
  };

  const removeBlockedSlot = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/blocked-slots/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Blocked slot removed');
      fetchBlockedSlots();
    } catch (error) {
      toast.error('Failed to remove blocked slot');
    }
  };

  // Get times based on selected date
  const getTimesForDate = () => {
    if (!selectedDate) return allTimes;
    const dayOfWeek = selectedDate.getDay();
    // Saturday (6) - only morning times up to 12:00
    if (dayOfWeek === 6) return saturdayTimes;
    return allTimes;
  };

  // Check if a time slot is blocked or booked
  const getSlotStatus = (time) => {
    if (!selectedDate) return 'available';
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    
    const isBlocked = blockedSlots.some(s => s.date === dateStr && s.time === time);
    if (isBlocked) return 'blocked';
    
    const isBooked = bookedSlots.includes(time);
    if (isBooked) return 'booked';
    
    return 'available';
  };

  return (
    <AdminLayout title="Blocked Time Slots">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Add Blocked Slot */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Block Time Slots</h2>
          <p className="text-sm text-slate-500 mb-2">
            Block time slots for home visits, leave, or other unavailability.
          </p>
          <p className="text-xs text-slate-400 mb-4">
            <strong>Tip:</strong> Hold Ctrl/Cmd and click to select multiple slots
          </p>
          
          <form onSubmit={addBlockedSlots} className="space-y-4">
            <div>
              <Label>Select Date</Label>
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => {
                  setSelectedDate(date);
                  setSelectedTimes([]);
                }}
                disabled={(date) => date < new Date() || date.getDay() === 0}
                className="rounded-md border mt-2"
                data-testid="blocked-slot-calendar"
              />
            </div>

            {selectedDate && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Select Time(s)</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={blockWholeDay}
                    className="text-xs"
                    data-testid="block-whole-day-btn"
                  >
                    <CalendarX className="w-3 h-3 mr-1" />
                    Block Whole Day
                  </Button>
                </div>
                {selectedDate.getDay() === 6 && (
                  <p className="text-xs text-amber-600 mb-2">Saturday: Slots available until 12:00 (practice closes at 13:00)</p>
                )}
                <div className="grid grid-cols-4 gap-2 mt-2">
                  {getTimesForDate().map((time) => {
                    const status = getSlotStatus(time);
                    const isSelected = selectedTimes.includes(time);
                    const isDisabled = status === 'blocked' || status === 'booked';
                    
                    return (
                      <button
                        key={time}
                        type="button"
                        onClick={(e) => handleTimeClick(time, e)}
                        disabled={isDisabled}
                        className={`time-slot text-sm ${
                          isDisabled 
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed opacity-60' 
                            : isSelected 
                              ? 'selected bg-[#5ce1e6] text-slate-900' 
                              : 'bg-slate-100 hover:bg-slate-200'
                        }`}
                        data-testid={`block-time-${time.replace(':', '')}`}
                        title={
                          status === 'blocked' ? 'Already blocked' : 
                          status === 'booked' ? 'Already booked' : 
                          'Click to select, Ctrl+Click for multiple'
                        }
                      >
                        {time}
                      </button>
                    );
                  })}
                </div>
                {selectedTimes.length > 0 && (
                  <p className="text-xs text-[#0e7490] mt-2">
                    Selected: {selectedTimes.sort().join(', ')}
                  </p>
                )}
              </div>
            )}

            <div>
              <Label>Reason (Optional)</Label>
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g., Home visit, Leave"
                data-testid="block-reason-input"
              />
            </div>

            <Button 
              type="submit" 
              className="w-full bg-[#5ce1e6] text-slate-900 hover:bg-[#22d3ee]"
              disabled={!selectedDate || selectedTimes.length === 0}
              data-testid="add-blocked-slot-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Block {selectedTimes.length > 1 ? `${selectedTimes.length} Slots` : 'Time Slot'}
            </Button>
          </form>
        </div>

        {/* Blocked Slots List */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Current Blocked Slots</h2>
          
          {loading ? (
            <p className="text-slate-500">Loading...</p>
          ) : blockedSlots.length === 0 ? (
            <p className="text-slate-500">No blocked slots</p>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {blockedSlots
                .sort((a, b) => {
                  if (a.date !== b.date) return a.date.localeCompare(b.date);
                  return a.time.localeCompare(b.time);
                })
                .map((slot) => (
                  <div 
                    key={slot.id} 
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                    data-testid={`blocked-slot-${slot.id}`}
                  >
                    <div>
                      <p className="font-medium text-slate-700">{slot.date} at {slot.time}</p>
                      <p className="text-sm text-slate-500">{slot.reason}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => removeBlockedSlot(slot.id)}
                      data-testid={`remove-blocked-${slot.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
            </div>
          )}

          {/* Info about permanent blocks */}
          <div className="mt-6 p-4 bg-[#f0fdfd] rounded-lg">
            <h3 className="font-medium text-[#0e7490] mb-2">Automatic Blocks</h3>
            <p className="text-sm text-slate-600">
              The following times are automatically blocked:
            </p>
            <ul className="text-sm text-slate-600 mt-2 list-disc list-inside">
              <li>Wednesday & Friday: 12:45 PM and 1:30 PM</li>
              <li>Saturdays: After 12:00 noon (practice closes at 13:00)</li>
              <li>Sundays: Closed</li>
            </ul>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
